import { type InferOutput, object } from "valibot";
import type { IncompleteIntegrationDefinition } from "../types";
import { initIntegrationDefinition } from "../Integration";
import ServiceAccountService from "../../services/ServiceAccountService";
import ServiceAccountSelectInput from "../../components/inputs/ServiceAccountSelectInput";
import { useTranslation } from "../../state/i18n";
import { errorTranslation, require, requireText, serviceAccount } from "../../utils/validation/base";
import { MAX_CHARACTERS_SM } from "../../constants";
import { TextInput } from "../../components/inputs/TextInput";
import { toFieldOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { createMemo } from "solid-js";
import type { SearchFieldProperties } from "../../fields/searchItems/SearchField";
import type { FieldType } from "../../types";
import FormServicesFactory from "../../services/FormServicesFactory";
import { isNil } from "lodash-es";
import { fieldReferenceOfTypes, valueReferenceOfTypes } from "../../utils/validation/references";
import FieldValueReference from "../../fields/FieldValueReference";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import FieldId from "../../fields/FieldId";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { SingleSelectWithSearchInput } from "../../components/menu/SingleSelectWithSearch";
import ServiceAccountId from "../../state/ServiceAccountId";
import VariableValueReference from "../../variables/VariableValueReference";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

const NEW_VALUE_FIELD_ALLOWED_FIELD_TYPES: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const NEW_VALUE_FIELD_ALLOWED_VARIABLE_TYPES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

type ConfigurationDto = {
  serviceAccount?: string;
  spreadsheetId?: string;
  sheetName: string;
  searchWidget?: string;
  newValueWidget?: string;
  columnName?: string;
};

const Schema = object({
  serviceAccount: serviceAccount(),
  spreadsheetId: requireText(MAX_CHARACTERS_SM),
  sheetName: requireText(MAX_CHARACTERS_SM),
  searchField: require(fieldReferenceOfTypes(["search"]), errorTranslation(
    keyFromSelector(($) => $.VALIDATION_NO_SELECTION),
  )),
  newValueField: require(valueReferenceOfTypes(
    NEW_VALUE_FIELD_ALLOWED_FIELD_TYPES,
    NEW_VALUE_FIELD_ALLOWED_VARIABLE_TYPES,
  ), errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION))),
  columnName: requireText(MAX_CHARACTERS_SM),
});

export type GoogleSheetsCellUpdateIntegrationConfiguration = InferOutput<typeof Schema>;
const GoogleSheetsCellUpdateIntegration: IncompleteIntegrationDefinition<
  GoogleSheetsCellUpdateIntegrationConfiguration,
  ConfigurationDto
> = {
  type: "googlesheetupdate",
  legacy: {
    namespace: "com.moreapps.plugins",
    key: "googlesheetupdate",
    version: 2,
  },
  schema: Schema,
  icon: integrationsIcons["googlesheetupdate"],
  titleKey: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: () => {
    const { t } = useTranslation();

    return {
      serviceAccount: undefined,
      spreadsheetId: "",
      sheetName: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SHEET_NAME_DEFAULT_VALUE),
      searchField: undefined,
      newValueField: undefined,
      columnName: undefined,
    };
  },
  propertyKeys: {
    serviceAccount: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_LABEL),
    spreadsheetId: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SPREADSHEET_ID_LABEL),
    sheetName: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SHEET_NAME_LABEL),
    searchField: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SEARCH_FIELD_LABEL),
    newValueField: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_NEW_VALUE_FIELD_LABEL),
    columnName: keyFromSelector(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_COLUMN_NAME_LABEL),
  },
  fromDto: (configuration, _fields, variables) => ({
    serviceAccount: isNil(configuration.serviceAccount)
      ? undefined
      : ServiceAccountId.from(configuration.serviceAccount),
    spreadsheetId: configuration.spreadsheetId,
    sheetName: configuration.sheetName,
    searchField: isNil(configuration.searchWidget) ? undefined : FieldId.from(configuration.searchWidget),
    newValueField: isNil(configuration.newValueWidget)
      ? undefined
      : variables.some((v) => v.uid.toString() === configuration.newValueWidget)
        ? VariableValueReference.from(configuration.newValueWidget)
        : FieldValueReference.from(configuration.newValueWidget),
    columnName: configuration.columnName,
  }),
  toDto: (configuration) => ({
    serviceAccount: configuration.serviceAccount?.toString(),
    spreadsheetId: configuration.spreadsheetId,
    sheetName: configuration.sheetName,
    searchWidget: isNil(configuration.searchField) ? undefined : configuration.searchField.toString(),
    newValueWidget: isNil(configuration.newValueField) ? undefined : configuration.newValueField.toString(),
    columnName: configuration.columnName,
  }),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = FormServicesFactory.forRootForm();
    const rootFormSearchFields = createMemo(() => fieldService.getFieldsByType<SearchFieldProperties>("search"));
    const newValueFieldAllowedRootFields = createMemo(() =>
      fieldService.getFieldsByTypes(NEW_VALUE_FIELD_ALLOWED_FIELD_TYPES),
    );
    const newValueFieldAllowedVariables = createMemo(() =>
      variableService.getVariablesByTypes(NEW_VALUE_FIELD_ALLOWED_VARIABLE_TYPES),
    );

    return (
      <>
        <ServiceAccountSelectInput
          type="GOOGLE_SHEETS"
          label={t(props.propertyKeys.serviceAccount)}
          value={props.integration.configuration.serviceAccount}
          fetchServiceAccounts={() => ServiceAccountService.getAll()}
          onChange={(value) => {
            props.updateConfiguration("serviceAccount", value);
          }}
          errorMessage={props.getError("serviceAccount")}
          required
          info={{
            title: t(props.propertyKeys.serviceAccount),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.serviceAccount,
            }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_DESCRIPTION),
          }}
        />
        <TextInput
          label={t(props.propertyKeys.spreadsheetId)}
          value={props.integration.configuration.spreadsheetId}
          onBlur={(e) => {
            props.updateConfiguration("spreadsheetId", e.target.value);
          }}
          errorMessage={props.getError("spreadsheetId")}
          required
          info={{
            title: t(props.propertyKeys.spreadsheetId),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.spreadsheetId,
            }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SPREADSHEET_ID_DESCRIPTION),
          }}
        />
        <TextInput
          label={t(props.propertyKeys.sheetName)}
          value={props.integration.configuration.sheetName}
          onBlur={(e) => {
            props.updateConfiguration("sheetName", e.target.value);
          }}
          errorMessage={props.getError("sheetName")}
          required
          info={{
            title: t(props.propertyKeys.sheetName),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.sheetName }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SHEET_NAME_DESCRIPTION),
          }}
        />
        <SingleSelectWithSearchInput
          label={t(props.propertyKeys.searchField)}
          value={props.integration.configuration.searchField}
          errorMessage={props.getError("searchField")}
          required
          options={createDynamicValueOptions(t, {
            fields: rootFormSearchFields().flatMap(toFieldOption),
            subforms: [],
            customVariables: [],
            metaVariables: [],
          })}
          onSelect={(value) => props.updateConfiguration("searchField", value)}
          onClear={() => props.updateConfiguration("searchField", null)}
          info={{
            title: t(props.propertyKeys.searchField),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.searchField }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_SEARCH_FIELD_DESCRIPTION),
          }}
          width="matchTrigger"
        />
        <SingleSelectWithSearchInput
          label={t(props.propertyKeys.newValueField)}
          value={props.integration.configuration.newValueField}
          errorMessage={props.getError("newValueField")}
          required
          options={createDynamicValueOptions(t, {
            fields: newValueFieldAllowedRootFields().flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: newValueFieldAllowedVariables().map(toVariableValueOption),
            metaVariables: [],
          })}
          onSelect={(value) => props.updateConfiguration("newValueField", value)}
          onClear={() => props.updateConfiguration("newValueField", null)}
          info={{
            title: t(props.propertyKeys.newValueField),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.newValueField,
            }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_NEW_VALUE_FIELD_DESCRIPTION),
          }}
          width="matchTrigger"
        />
        <TextInput
          label={t(props.propertyKeys.columnName)}
          value={props.integration.configuration.columnName}
          onBlur={(e) => {
            props.updateConfiguration("columnName", e.target.value);
          }}
          errorMessage={props.getError("columnName")}
          required
          info={{
            title: t(props.propertyKeys.columnName),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.columnName }),
            description: t(($) => $.GOOGLE_SHEETS_CELL_UPDATE_INTEGRATION_CONFIGURATION_COLUMN_NAME_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(GoogleSheetsCellUpdateIntegration);
